import { Link } from 'react-router-dom';

export default function SeoContentSection() {
  return (
    <section id="resources" className="py-20 md:py-28 bg-background-100">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="text-xs font-medium text-primary-500 tracking-widest uppercase">
            Learn &amp; Discover
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mt-3">
            Master the{' '}
            <span className="italic font-normal">Hipobuy Spreadsheet</span>
          </h2>
          <p className="text-foreground-500 mt-3 max-w-xl mx-auto text-sm">
            Everything you need to know about spreadsheet-based shopping research — from beginner tutorials to advanced product discovery strategies.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <div className="lg:col-span-1">
            <Link
              to="/hipobuy-spreadsheet-guide"
              className="block bg-foreground-950 text-background-50 rounded-3xl p-8 h-full card-hover cursor-pointer"
            >
              <span className="inline-block text-xs font-medium text-accent-400 tracking-widest uppercase mb-4">
                Featured Guide
              </span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold leading-tight">
                Hipobuy Spreadsheet: The Complete Beginner&apos;s Guide 2026
              </h3>
              <p className="text-background-50/70 mt-4 text-sm leading-relaxed">
                Learn everything from setting up your first product research spreadsheet to advanced comparison techniques. Our comprehensive guide walks you through the entire process with step-by-step instructions, practical examples, and expert tips to help you become a more efficient and informed shopper.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-accent-300 font-medium text-sm">
                Read Full Guide
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </div>
            </Link>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                title: 'Why Hipobuy Spreadsheet Matters',
                desc: 'Discover why thousands of shoppers are switching to spreadsheet-based product research for faster discovery, better comparisons, and smarter buying decisions in 2026.',
                icon: 'ri-question-answer-line',
              },
              {
                title: 'Spreadsheet vs. Manual Browsing',
                desc: 'Compare the efficiency of organized spreadsheet research against traditional manual browsing. See how structured data transforms your shopping experience.',
                icon: 'ri-bar-chart-box-line',
                link: '/kaspreadsheet-vs-traditional',
              },
              {
                title: 'Product Categories Explained',
                desc: 'Navigate through popular shopping categories including footwear, apparel, accessories, and more — each organized for quick side-by-side comparison.',
                icon: 'ri-price-tag-3-line',
                link: '/kaspreadsheet-shopping-guide',
              },
              {
                title: 'Tips for Beginner Shoppers',
                desc: 'New to spreadsheet shopping? Get started with our beginner-friendly tips covering basic navigation, product verification, and how to read comparison tables.',
                icon: 'ri-lightbulb-line',
                link: '/kaspreadsheet-product-research',
              },
            ].map((item) => (
              item.link ? (
                <Link key={item.title} to={item.link} className="bg-background-50 rounded-2xl p-6 card-hover cursor-pointer">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h4 className="font-heading text-lg font-bold text-foreground-950">{item.title}</h4>
                  <p className="text-sm text-foreground-500 mt-2 leading-relaxed">{item.desc}</p>
                </Link>
              ) : (
                <div key={item.title} className="bg-background-50 rounded-2xl p-6 card-hover cursor-default">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h4 className="font-heading text-lg font-bold text-foreground-950">{item.title}</h4>
                  <p className="text-sm text-foreground-500 mt-2 leading-relaxed">{item.desc}</p>
                </div>
              )
            ))}
          </div>
        </div>

        <div className="bg-background-50 rounded-3xl p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">
                Comparison:{' '}
                <span className="italic font-normal">Normal Searching vs. Hipobuy Spreadsheet Method</span>
              </h3>
              <p className="text-foreground-500 text-sm mt-3 leading-relaxed">
                See how the spreadsheet approach stacks up against traditional browsing across every key shopping dimension.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-3 pr-4 text-foreground-700 font-medium">Feature</th>
                    <th className="text-left py-3 pr-4 text-foreground-400">Normal</th>
                    <th className="text-left py-3 text-accent-600 font-medium">Spreadsheet</th>
                  </tr>
                </thead>
                <tbody className="text-foreground-600">
                  <tr className="border-b border-background-200/60">
                    <td className="py-3 pr-4 font-medium text-foreground-700">Discovery Speed</td>
                    <td className="py-3 pr-4">Slow manual search</td>
                    <td className="py-3 text-accent-600">Organized database</td>
                  </tr>
                  <tr className="border-b border-background-200/60">
                    <td className="py-3 pr-4 font-medium text-foreground-700">Comparison</td>
                    <td className="py-3 pr-4">Difficult to compare</td>
                    <td className="py-3 text-accent-600">Easy side-by-side</td>
                  </tr>
                  <tr className="border-b border-background-200/60">
                    <td className="py-3 pr-4 font-medium text-foreground-700">Research Effort</td>
                    <td className="py-3 pr-4">Very time consuming</td>
                    <td className="py-3 text-accent-600">Structured workflow</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 font-medium text-foreground-700">Beginner Ease</td>
                    <td className="py-3 pr-4">Complex and confusing</td>
                    <td className="py-3 text-accent-600">Simple step-by-step</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}